import { useContext, useEffect, useMemo, useState } from 'react';

import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import PerformanceBoardSlideOutContext from 'components/PerformanceBoards/contexts/PerformanceBoardSlideOutContext';
import getBonusMetrics, {
  BonusMetricAssignment,
} from 'screens/Settings/Advanced/ManageBonusMetrics/api/getBonusMetrics';

/**
 * Loads the bonus metric assignments for the slide-out board's bonus program
 * (`GET /v1/driver_bonus/metrics`). Returns the raw list plus a `fieldName`
 * lookup. `programId` is undefined when the board is not a bonus board (no
 * program), in which case no request is made.
 */
const useBonusMetricAssignments = () => {
  const { api } = useContext(CloudFunctionClientContext);
  const { getBonusProgram } = useContext(DatasetDefinitionsContext);
  const { board } = useContext(PerformanceBoardSlideOutContext);

  const programId = getBonusProgram(board.dataType)?.id;

  const [assignments, setAssignments] = useState<BonusMetricAssignment[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isActive = true;

    if (!programId) {
      setAssignments([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    getBonusMetrics({ api, driverBonusProgramId: programId }).then(
      (response) => {
        if (!isActive) {
          return;
        }
        setAssignments(
          response.ok && response.data ? response.data.metrics : [],
        );
        setIsLoading(false);
      },
    );

    return () => {
      isActive = false;
    };
  }, [api, programId]);

  const byFieldName = useMemo(() => {
    const map: { [fieldName: string]: BonusMetricAssignment } = {};
    assignments.forEach((assignment) => {
      map[assignment.fieldName] = assignment;
    });
    return map;
  }, [assignments]);

  return { assignments, byFieldName, isLoading, programId };
};

export default useBonusMetricAssignments;
